import { useEffect, useState } from "react";
import api, { fmtUsd } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from "recharts";
import { TrendUp, TrendDown } from "@phosphor-icons/react";

export default function PnLDialog({ open, onOpenChange }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/pnl/history").then((r) => setData(r.data)).catch(() => setData(null));
  }, [open]);

  const pnl = data ? data.current - data.start : 0;
  const pos = pnl >= 0;
  const equity = (data?.equity || []).map((e, i) => ({ ...e, i }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#09090b] border-[#27272a] rounded-none max-w-2xl" data-testid="pnl-dialog">
        <DialogHeader>
          <DialogTitle className="font-head font-semibold tracking-tight">Performance</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3">
          <div className="border border-[#27272a] p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#a1a1aa]">Equity</p>
            <p className="font-mono text-lg font-semibold">{fmtUsd(data?.current ?? 0)}</p>
          </div>
          <div className="border border-[#27272a] p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#a1a1aa]">Total PnL</p>
            <p className={`font-mono text-lg font-semibold flex items-center gap-1 ${pos ? "text-green-400" : "text-red-400"}`}>
              {pos ? <TrendUp size={16} /> : <TrendDown size={16} />}{pos ? "+" : ""}{fmtUsd(pnl)}
            </p>
          </div>
          <div className="border border-[#27272a] p-3">
            <p className="text-[10px] uppercase tracking-wide text-[#a1a1aa]">Closed Trades</p>
            <p className="font-mono text-lg font-semibold">{data?.trades ?? 0}</p>
          </div>
        </div>

        <div>
          <p className="text-xs text-[#a1a1aa] mb-2">Equity Curve</p>
          <div className="h-48 border border-[#27272a] p-2" data-testid="equity-chart">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={equity} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="eq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="i" hide />
                <YAxis domain={["auto", "auto"]} tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "JetBrains Mono" }} width={50} />
                <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #3f3f46", borderRadius: 0, fontSize: 11 }}
                  labelStyle={{ display: "none" }} formatter={(v) => [fmtUsd(v), "equity"]} />
                <ReferenceLine y={data?.start} stroke="#3f3f46" strokeDasharray="3 3" />
                <Area type="monotone" dataKey="equity" stroke="#06b6d4" strokeWidth={2} fill="url(#eq)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div>
          <p className="text-xs text-[#a1a1aa] mb-2">Daily Realized PnL</p>
          <div className="h-36 border border-[#27272a] p-2" data-testid="daily-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.daily || []} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                <XAxis dataKey="date" tick={{ fill: "#a1a1aa", fontSize: 9, fontFamily: "JetBrains Mono" }} tickFormatter={(d) => d?.slice(5)} />
                <YAxis tick={{ fill: "#a1a1aa", fontSize: 10, fontFamily: "JetBrains Mono" }} width={50} />
                <Tooltip contentStyle={{ background: "#09090b", border: "1px solid #3f3f46", borderRadius: 0, fontSize: 11 }}
                  cursor={{ fill: "#18181b" }} formatter={(v) => [fmtUsd(v), "pnl"]} />
                <ReferenceLine y={0} stroke="#3f3f46" />
                <Bar dataKey="pnl" isAnimationActive={false}>
                  {(data?.daily || []).map((e, i) => <Cell key={i} fill={e.pnl >= 0 ? "#22c55e" : "#ef4444"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {(!data || data.trades === 0) && (
          <p className="text-[11px] text-[#52525b] font-mono text-center">No closed trades yet — charts populate as positions close.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
